import { useState } from "react";
import { Expand, X } from "lucide-react";
import useAccessibleOverlay from "@/hooks/useAccessibleOverlay";
import { trackPublicFeatureEvent } from "@/lib/publicFeatureEvents";

export default function FeatureVisual({ image, alt, caption, page, featureId, priority = false }) {
  const [open, setOpen] = useState(false);
  const { panelRef, triggerRef } = useAccessibleOverlay(open, () => setOpen(false));

  function show() {
    setOpen(true);
    trackPublicFeatureEvent("public_feature_visual_opened", { page, feature: featureId });
  }

  return (
    <>
      <figure className="min-w-0">
        <button
          type="button"
          ref={triggerRef}
          onClick={show}
          className="group relative block w-full overflow-hidden rounded-2xl border border-slate-200 bg-slate-100 shadow-[0_18px_60px_rgba(15,23,42,0.10)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-700 focus-visible:ring-offset-4"
          aria-label={`Enlarge screenshot: ${alt}`}
        >
          <img
            src={image}
            alt={alt}
            width="1280"
            height="720"
            loading={priority ? "eager" : "lazy"}
            fetchPriority={priority ? "high" : "auto"}
            className="aspect-[16/9] w-full object-cover object-top transition-transform duration-300 motion-reduce:transition-none group-hover:scale-[1.01]"
          />
          <span className="absolute bottom-3 right-3 inline-flex min-h-11 items-center gap-2 rounded-full border border-white/80 bg-white/95 px-3 py-2 text-xs font-semibold text-slate-800 shadow-sm">
            <Expand className="h-4 w-4" aria-hidden="true" /> Enlarge
          </span>
        </button>
        {caption && <figcaption className="mt-3 text-sm leading-relaxed text-slate-500">{caption}</figcaption>}
      </figure>

      {open && (
        <div className="fixed inset-0 z-[90] grid place-items-center p-3 sm:p-6" role="presentation">
          <button
            type="button"
            className="absolute inset-0 bg-slate-950/80 backdrop-blur-sm"
            onClick={() => setOpen(false)}
            data-overlay-backdrop
            aria-label="Close enlarged screenshot"
          />
          <section
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-label={`Enlarged product screenshot: ${alt}`}
            tabIndex={-1}
            className="relative z-10 w-full max-w-7xl rounded-2xl border border-white/20 bg-white p-2 shadow-2xl sm:p-3"
          >
            <button
              type="button"
              data-autofocus
              onClick={() => setOpen(false)}
              className="absolute right-4 top-4 z-10 inline-flex min-h-11 min-w-11 items-center justify-center rounded-full bg-slate-950/80 text-white hover:bg-slate-950"
              aria-label="Close enlarged screenshot"
            >
              <X className="h-5 w-5" aria-hidden="true" />
            </button>
            <img src={image} alt={alt} className="max-h-[82vh] w-full rounded-xl object-contain" />
            {caption && <p className="px-2 pb-1 pt-3 text-sm text-slate-600">{caption}</p>}
          </section>
        </div>
      )}
    </>
  );
}
